import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-normal rounded-md text-sm leading-snug font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      width: {
        standard: "app-text-button",
        icon: "app-icon-button w-9 shrink-0 px-0",
        control: "",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      width: "standard",
    },
  }
)

const hasText = (children) => React.Children.toArray(children).some((child) => {
  if (typeof child === "string") return child.trim().length > 0;
  if (typeof child === "number") return true;
  if (!React.isValidElement(child) || child.type === "svg" || child.props["aria-hidden"] === true || /(^|\s)sr-only(\s|$)/.test(child.props.className || "")) return false;
  return hasText(child.props.children);
});

const textOf = (children) => React.Children.toArray(children).map((c) => (typeof c === "string" || typeof c === "number" ? String(c) : React.isValidElement(c) && c.type !== "svg" ? textOf(c.props.children) : "")).join(" ").replace(/\s+/g, " ").trim();
// Pulsanti che fanno avanzare un form: sempre verdi e allineati a destra in tutto il sito
const FORWARD = /^(avanti|continua|prosegui|procedi)\b/i;
const PRIMARY = /^(conferma|invia|invio|salva|paga|pagamento|acquista|completa|firma|emetti|pubblica|richiedi|elaborazione|ordina sim|registrati|accedi)/i;
const IN_FORM = /^(crea|aggiungi|registra|attiva|genera|ordina|prenota|carica|applica|aggiorna|ricarica|calcola|verifica|assegna|approva|riattiva|chiedi|usa|cerca|trova|scarica e)/i;
const FORM_SCOPE = "form, [role=dialog], [role=alertdialog]";

const Button = React.forwardRef(({ className, variant, size, asChild = false, next, ...props }, ref) => {
  const textButton = React.useRef(false);
  const element = React.useRef(null);
  const [renderedText, setRenderedText] = React.useState(false);
  const [inForm, setInForm] = React.useState(false);
  textButton.current = textButton.current || hasText(props.children) || renderedText;
  const label = textOf(props.children);
  const attachRef = React.useCallback((node) => {
    element.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  }, [ref]);
  React.useLayoutEffect(() => {
    if (size === "icon" || textButton.current || !element.current) return;
    const copy = element.current.cloneNode(true);
    copy.querySelectorAll('svg, .sr-only, [aria-hidden="true"]').forEach((node) => node.remove());
    if (copy.textContent.trim() || element.current.querySelector("svg.animate-spin")) setRenderedText(true);
  }, [props.children, size, renderedText]);
  React.useLayoutEffect(() => {
    if (element.current && IN_FORM.test(label)) setInForm(!!element.current.closest(FORM_SCOPE));
  }, [label]);
  const testId = React.useId();
  const compact = size === "icon" || !textButton.current;
  const width = props.role === "combobox" ? "control" : compact ? "icon" : "standard";
  const plain = !variant || variant === "default";
  const toggle = props.role === "tab" || props["aria-pressed"] !== undefined || props.role === "combobox";
  const okClass = /(^|\s)btn-ok(\s|$)/.test(className || "");
  const isNext = next ?? (!compact && !toggle && variant !== "destructive" && variant !== "link"
    && (okClass || FORWARD.test(label) || (plain && (props.type === "submit" || PRIMARY.test(label) || (inForm && IN_FORM.test(label))))));
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size: size || (compact ? "icon" : "default"), width, className }), isNext && "app-next-button")}
      data-button-size={size || "default"}
      data-testid={props["data-testid"] || (asChild && props.children?.props?.["data-testid"]) || `button-${testId}`}
      ref={attachRef}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
